import { ReactNode, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";

import { useAnalytics } from "analytics/AnalyticsContext";
import NoticeBanner from "components/NoticeBanner";
import {
  afterCodeSent,
  CODE_RESENT_NOTICE,
  resendOnEndedCode,
  type EndedCodeResend,
  type NewCodeOutcome,
} from "components/OtpVerification/ended-code";
import { useUser } from "context/UserContext";
import { useFirebase } from "firebase";
import { useElapsedTime } from "hooks/useElapsedTime";
import type { LoginGrantState } from "helpers/login-grant";
import {
  recordOtpSession,
  restartOtpSessions,
  type OtpSessions,
} from "helpers/otp-sessions";
import { freshRecaptchaToken, loadRecaptchaSiteKey } from "helpers/recaptcha";
import { NetworkError } from "hooks/usePublicAPI";
import AuthLayout from "layouts/AuthLayout";
import { AuthShellProvider } from "layouts/AuthShellContext";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import {
  leadFlowEvents,
  type ConfirmFailureReason,
  type ConfirmSurface,
} from "pages/StartLead/events";
import { apiErrorCode } from "utils/errors";

import {
  activeSendRefusal,
  readGrantSend,
  recordGrantSend,
  recordSendRefusal,
  resumableSend,
  sendTrigger,
  type GrantSend,
} from "./grant-send";
import FixRow from "./FixRow";
import type {
  DestinationChannel,
  DestinationCorrector,
} from "./destination-edit";
import { postGrantRequest } from "./login-grant-api";
import {
  autoSendPlan,
  claimAutoSend,
  failureFallbackFor,
  emailSendFailureKind,
  failureReasonFor,
  grantFailureKind,
  grantFailureMessage,
  parseGrantCompletion,
  primaryMethod,
  SEND_NOT_STARTED,
  smsSendFailureKind,
  SUPPORT_LINES,
  supportLineKindFor,
  verifySmsOtpBody,
  type GrantCompletion,
  type GrantConfirmReport,
  type GrantFailure,
  type GrantFailureKind,
  type GrantLookup,
  type GrantMethod,
  type RecaptchaStatus,
} from "./login-grant-flow";
import { useCooldownSeconds } from "./useCooldown";
import {
  askedFailureFallback,
  callLineEmphasis,
  codeInputState,
  codeMessage,
  destinationPhase,
  emailEmphasis,
  isCodeExpired,
  resendState,
  sendFailureOf,
  sendFailureSlot,
  titleFor,
  type ChannelState,
  type SendFailure,
  type VerifyFailure,
} from "./verify-screen";
import {
  DestinationLine,
  DestinationSlot,
  EmailVerifyBody,
  TextVerifyBody,
} from "./VerifyScreen";

type RequestSmsOtpResponse = { message: string; sessionInfo?: string };

function failureFrom(err: unknown): GrantFailure {
  if (err instanceof NetworkError) {
    const kind = grantFailureKind(apiErrorCode(err), err.statusCode);
    return { kind, reason: failureReasonFor(kind, true) };
  }
  Sentry.captureException(err);
  return { kind: "other", reason: failureReasonFor("other", false) };
}

function smsSendFailureFrom(err: unknown): GrantFailure {
  const failure = failureFrom(err);
  return { ...failure, kind: smsSendFailureKind(failure.kind) };
}

function emailSendFailureFrom(err: unknown): GrantFailure {
  const failure = failureFrom(err);
  return { ...failure, kind: emailSendFailureKind(failure.kind) };
}

type FrameProps = {
  title: string;
  subheader: ReactNode;
  headerPhone: { href: string; label: string };
  children: ReactNode;
};

/** Where a confirm screen sits: the sign-in page's own chrome, or, embedded,
 * a heading and body that fit into the screen that hosts them. */
function GrantFrame({
  embedded,
  title,
  subheader,
  headerPhone,
  children,
}: FrameProps & { embedded: boolean }) {
  if (!embedded) {
    return (
      <AuthLayout
        title={title}
        description=""
        subheader={subheader}
        headerPhone={headerPhone}
        bodySpacing="screen"
      >
        {children}
      </AuthLayout>
    );
  }
  return (
    <AuthShellProvider value="quote_flow">
      <div>
        <h2 className="font-serif text-[21px] font-normal text-soil">
          {title}
        </h2>
        <div className="mt-1.5">{subheader}</div>
        <div className="mt-6">{children}</div>
      </div>
    </AuthShellProvider>
  );
}

function recaptchaStatusOf(
  key: string | null,
  failed: boolean,
): RecaptchaStatus {
  if (key) return "ready";
  return failed ? "failed" : "loading";
}

/** Grants whose automatic send has started in this page session, so a
 * remounted screen never sends a second code or link. */
const autoSentGrants = new Set<string>();

/**
 * Confirms the grant's destination. On arrival it sends the texted code
 * (preferred) or the emailed link, and the screen for that channel is on
 * show from the first paint: its controls only relabel, enable or restyle as
 * the send goes, fails or is repeated. Switching channel is the one change of
 * screen. A screen reopened in a tab that already sent for this grant (a
 * reload) resumes on that code or link and sends another only when the
 * visitor asks.
 */
export function LoginGrantConfirm({
  grant,
  lookup,
  surface,
  onInvalid,
  embedded = false,
  onCompletion,
  onSignedIn,
  report,
  onChangeDestination,
  onCorrect,
}: {
  grant: LoginGrantState;
  lookup: GrantLookup;
  /** The screen hosting this one, reported with each of its events. */
  surface: ConfirmSurface;
  /** Called when the grant has ended, with the step it ended on. */
  onInvalid: (stage: "send" | "verify") => void;
  /** Sits inside the host screen instead of taking over the page, and
   * leaves the visitor where they are once signed in. */
  embedded?: boolean;
  /** Called when the proof leads somewhere other than a sign-in. */
  onCompletion?: (
    completion: Extract<
      GrantCompletion,
      { kind: "create_account" | "use_account_channel" | "not_found" }
    >,
  ) => void;
  /** Called as soon as a texted code has signed the visitor in. */
  onSignedIn?: () => void;
  /** Hears of each send, acceptance and failure on this screen. */
  report?: GrantConfirmReport;
  /** Offers the way back to the screen that asked for the destination. */
  onChangeDestination?: () => void;
  /** Saves a corrected destination. The fix row is offered when this is given
   * and the grant carries a proof that allows it. */
  onCorrect?: DestinationCorrector;
}) {
  const { loginGrant, leadKind, method: chosenMethod } = grant;
  const chosen = chosenMethod ?? null;
  const supportKind = supportLineKindFor(leadKind);
  const supportLine = SUPPORT_LINES[supportKind];

  const navigate = useNavigate();
  const { track } = useAnalytics();
  const trackEvent = useTrackLeadFlowEvent();
  const { auth, user: firebaseUser, signInWithCustomToken } = useFirebase();
  const { user: apiUser } = useUser();

  const [resumed] = useState(() =>
    resumableSend(readGrantSend(loginGrant), lookup),
  );
  const [lastSend, setLastSend] = useState<GrantSend | null>(resumed);
  const [method, setMethod] = useState<GrantMethod>(
    resumed?.method ?? primaryMethod(lookup, chosen),
  );
  const [verifyFailure, setVerifyFailure] = useState<VerifyFailure | null>(
    null,
  );
  const [refusedAt, setRefusedAt] = useState<number | null>(() =>
    resumed === null ? activeSendRefusal(loginGrant, Date.now()) : null,
  );
  const [sendFailure, setSendFailure] = useState<SendFailure | null>(() =>
    refusedAt === null ? null : "too_many",
  );
  const [otherChannelError, setOtherChannelError] = useState<{
    asked: GrantMethod;
    message: string;
  } | null>(null);
  const [sendingMethod, setSendingMethod] = useState<GrantMethod | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [sessions, setSessions] = useState<OtpSessions | null>(() =>
    resumed?.method === "sms_otp"
      ? { current: resumed.sessionInfo, previous: resumed.previousSessionInfo }
      : null,
  );
  const [recaptchaKey, setRecaptchaKey] = useState<string | null>(null);
  const [recaptchaKeyFailed, setRecaptchaKeyFailed] = useState(false);
  const [arriving, setArriving] = useState<GrantMethod | null>(() =>
    resumed !== null || refusedAt !== null || autoSentGrants.has(loginGrant)
      ? null
      : primaryMethod(lookup, chosen),
  );
  const [codeNotice, setCodeNotice] = useState("");
  const endedResend = useRef<EndedCodeResend>(afterCodeSent());
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  // Each send takes the next number; a send that finds a newer one has been
  // replaced by the visitor's switch of method and must change nothing.
  const sendEpoch = useRef(0);

  const needsRecaptcha = lookup.methods.includes("sms_otp");

  useEffect(() => {
    if (!needsRecaptcha || recaptchaKey || recaptchaKeyFailed) return;
    loadRecaptchaSiteKey()
      .then(setRecaptchaKey)
      .catch((err: unknown) => {
        if (!(err instanceof NetworkError)) Sentry.captureException(err);
        setRecaptchaKeyFailed(true);
      });
  }, [needsRecaptcha, recaptchaKey, recaptchaKeyFailed]);

  const emailOffered = lookup.methods.includes("email_link");
  const textOffered = lookup.methods.includes("sms_otp");
  const askedFailureMessage = (kind: GrantFailureKind, asked: GrantMethod) =>
    grantFailureMessage(kind, askedFailureFallback(asked, emailOffered));

  const reportFailure = (
    started: GrantMethod,
    reason: ConfirmFailureReason,
  ) => {
    trackEvent(
      leadFlowEvents.confirmFailed({
        purpose: lookup.purpose,
        surface,
        method: started,
        reason,
      }),
    );
    report?.failed(reason);
  };

  const handleFailure = (
    { kind, reason }: GrantFailure,
    started: GrantMethod,
    stage: "send" | "verify",
    afterCodeEnded = false,
  ) => {
    reportFailure(started, reason);
    if (kind === "grant_invalid") {
      onInvalid(stage);
      return;
    }
    if (afterCodeEnded && kind === "rate_limited") {
      setVerifyFailure("resend_refused");
    }
    if (stage === "verify") {
      setVerifyFailure(kind);
      return;
    }
    if (sendFailureSlot(started, method) === "button") {
      setOtherChannelError({
        asked: started,
        message: askedFailureMessage(kind, started),
      });
      return;
    }
    const failure = sendFailureOf(started, kind);
    setSendFailure(failure);
    if (failure === "too_many") {
      const at = Date.now();
      recordSendRefusal(loginGrant, at);
      setRefusedAt(at);
    }
  };

  const startSend = (started: GrantMethod) => {
    setVerifyFailure(null);
    setOtherChannelError(null);
    setSendingMethod(started);
  };

  const sendSmsCode = async (
    automatic: boolean,
    codeEnded = false,
  ): Promise<boolean> => {
    const epoch = ++sendEpoch.current;
    const superseded = () => epoch !== sendEpoch.current;
    const trigger = sendTrigger({
      codeEnded,
      automatic,
      method: "sms_otp",
      previous: lastSend,
      leading: primaryMethod(lookup, chosen),
    });
    startSend("sms_otp");
    try {
      const token = await freshRecaptchaToken(recaptchaRef);
      if (superseded()) return false;
      if (!token.ok) {
        handleFailure(SEND_NOT_STARTED, "sms_otp", "send", codeEnded);
        return false;
      }
      const response = await postGrantRequest<RequestSmsOtpResponse>(
        "/auth/request-sms-otp",
        { login_grant: loginGrant, recaptchaToken: token.token },
      );
      if (superseded()) return false;
      if (!response.sessionInfo) {
        handleFailure(
          { kind: "send_failed", reason: "other" },
          "sms_otp",
          "send",
          codeEnded,
        );
        return false;
      }
      setSendFailure(null);
      const sent = response.sessionInfo;
      const send = recordGrantSend(
        loginGrant,
        { method: "sms_otp", sessionInfo: sent, replacesEnded: codeEnded },
        Date.now(),
      );
      setSessions((held) =>
        codeEnded ? restartOtpSessions(sent) : recordOtpSession(held, sent),
      );
      endedResend.current = afterCodeSent();
      setLastSend(send);
      setMethod("sms_otp");
      trackEvent(
        leadFlowEvents.confirmStarted({
          method: "sms_otp",
          purpose: lookup.purpose,
          surface,
          trigger,
          sendCount: send.count,
        }),
      );
      report?.sent("sms_otp", trigger);
      return true;
    } catch (err) {
      if (superseded()) return false;
      handleFailure(smsSendFailureFrom(err), "sms_otp", "send", codeEnded);
      return false;
    } finally {
      if (!superseded()) setSendingMethod(null);
    }
  };

  const sendMagicLink = async (automatic: boolean): Promise<boolean> => {
    const epoch = ++sendEpoch.current;
    const superseded = () => epoch !== sendEpoch.current;
    const trigger = sendTrigger({
      automatic,
      method: "email_link",
      previous: lastSend,
      leading: primaryMethod(lookup, chosen),
    });
    startSend("email_link");
    try {
      await postGrantRequest<unknown>("/auth/request-magic-link", {
        login_grant: loginGrant,
      });
      if (superseded()) return false;
      setSendFailure(null);
      const send = recordGrantSend(
        loginGrant,
        { method: "email_link" },
        Date.now(),
      );
      setLastSend(send);
      setMethod("email_link");
      trackEvent(
        leadFlowEvents.confirmStarted({
          method: "email_link",
          purpose: lookup.purpose,
          surface,
          trigger,
          sendCount: send.count,
        }),
      );
      report?.sent("email_link", trigger);
      return true;
    } catch (err) {
      if (superseded()) return false;
      handleFailure(emailSendFailureFrom(err), "email_link", "send");
      return false;
    } finally {
      if (!superseded()) setSendingMethod(null);
    }
  };

  const requestSmsCode = () => void sendSmsCode(false);
  const requestMagicLink = () => void sendMagicLink(false);

  const recaptchaStatus = recaptchaStatusOf(recaptchaKey, recaptchaKeyFailed);
  const sending = sendingMethod ?? arriving;
  const delivered = lastSend?.method === method;
  const textBlocked = method === "sms_otp" && recaptchaKey === null;
  const shownFailure =
    sendFailure ??
    (method === "sms_otp" && recaptchaKeyFailed && !delivered
      ? "failed"
      : null);
  const channel: ChannelState = {
    method,
    sending: sending === method,
    delivered,
    failure: shownFailure,
  };

  const elapsedMs = useElapsedTime({ active: !delivered, intervalMs: 500 });
  const cooldownSeconds = useCooldownSeconds(
    Math.max(lastSend?.sentAt ?? 0, refusedAt ?? 0) || null,
  );

  useEffect(() => {
    if (arriving === null) return;
    const plan = autoSendPlan(lookup, recaptchaStatus, chosen);
    if (plan === "wait") return;
    if (plan === "none") {
      setArriving(null);
      return;
    }
    if (!claimAutoSend(autoSentGrants, loginGrant)) return;
    setArriving(plan);
    const send = plan === "sms_otp" ? sendSmsCode : sendMagicLink;
    void send(true).finally(() => setArriving(null));
    // The send reads the latest handlers through this render's closure; it
    // is claimed once per grant, so later renders never repeat it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arriving, recaptchaStatus]);

  const shownScreen = arriving === null ? (lastSend?.method ?? null) : null;
  const shownScreenRef = useRef<GrantMethod | null>(null);
  const resumedScreenRef = useRef(resumed !== null);
  useEffect(() => {
    if (shownScreenRef.current === shownScreen) return;
    shownScreenRef.current = shownScreen;
    if (shownScreen === null) return;
    trackEvent(
      leadFlowEvents.confirmViewed({
        purpose: lookup.purpose,
        surface,
        method: shownScreen,
        resumed: resumedScreenRef.current,
      }),
    );
    resumedScreenRef.current = false;
    // Reported once per code or link screen the visitor is shown, however
    // many times the effect runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownScreen]);

  /** A code the provider ended earns one new text, sent by the screen; once
   * that is spent, the visitor's Resend is the way on. Resolves
   * `new_code_sent` when the text went. */
  const replaceEndedCode = async (): Promise<NewCodeOutcome> => {
    const decision = resendOnEndedCode(endedResend.current);
    endedResend.current = decision.next;
    if (!decision.sendNow) {
      setVerifyFailure("code_expired");
      return undefined;
    }
    const sent = await sendSmsCode(false, true);
    if (!sent) return undefined;
    setCodeNotice(CODE_RESENT_NOTICE);
    return "new_code_sent";
  };

  const verifyCode = async (code: string): Promise<NewCodeOutcome> => {
    setVerifyFailure(null);
    setCodeNotice("");
    if (!sessions) {
      reportFailure("sms_otp", "code_expired");
      setVerifyFailure("code_expired");
      return undefined;
    }
    let codeEnded = false;
    setIsVerifying(true);
    try {
      const completion = parseGrantCompletion(
        await postGrantRequest<unknown>(
          "/auth/verify-sms-otp",
          verifySmsOtpBody(loginGrant, sessions, code),
        ),
      );
      if (completion.kind === "failed") {
        reportFailure("sms_otp", "other");
        setVerifyFailure("other");
        return undefined;
      }
      if (completion.kind !== "signed_in") {
        if (onCompletion) {
          trackEvent(
            leadFlowEvents.confirmAccepted({
              method: "sms_otp",
              purpose: lookup.purpose,
              surface,
            }),
          );
          report?.accepted("sms_otp");
          onCompletion(completion);
        } else {
          reportFailure("sms_otp", "other");
          setVerifyFailure("other");
        }
        return undefined;
      }
      try {
        await signInWithCustomToken(auth, completion.customToken);
      } catch (signInError) {
        Sentry.captureException(signInError);
        reportFailure("sms_otp", "sign_in_failed");
        setVerifyFailure("sign_in_failed");
        return undefined;
      }
      onSignedIn?.();
      mutate("/me");
      mutate("/me/home");
      trackEvent(
        leadFlowEvents.confirmAccepted({
          method: "sms_otp",
          purpose: lookup.purpose,
          surface,
        }),
      );
      report?.accepted("sms_otp");
      if (lookup.purpose === "account_from_quote") {
        track("quote_flow_account_confirmed", { method: "sms_otp" });
      }
      if (!embedded) navigate(lookup.redirectPath, { replace: true });
    } catch (err) {
      const failure = failureFrom(err);
      if (failure.kind === "code_expired") {
        reportFailure("sms_otp", failure.reason);
        codeEnded = true;
      } else {
        handleFailure(failure, "sms_otp", "verify");
      }
    } finally {
      setIsVerifying(false);
    }
    if (codeEnded) return replaceEndedCode();
    return undefined;
  };

  const destination =
    (method === "sms_otp" ? lookup.phoneDisplay : lookup.emailDisplay) ?? "";

  const destinationNotice = (): ReactNode => {
    if (!firebaseUser) return null;
    const name = [apiUser?.first_name, apiUser?.last_name]
      .filter(Boolean)
      .join(" ");
    return (
      <NoticeBanner>
        You&rsquo;re signed in{name ? ` as ${name}` : ""}. To continue we need
        to confirm {destination}. If it belongs to a different account,
        you&rsquo;ll be switched to that account once you confirm.
      </NoticeBanner>
    );
  };

  // The invisible widget backs every code request, including a resend, so
  // both layouts mount it. Its badge is hidden; the text layout carries the
  // disclosure Google asks for in its place. It is out of flow: a block here
  // would stop the code box's top margin collapsing into the gap above it, and
  // the box would drop when the widget mounts.
  const recaptchaWidget = recaptchaKey ? (
    <div className="absolute h-0 overflow-hidden [&_.grecaptcha-badge]:invisible">
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        badge="inline"
        sitekey={recaptchaKey}
      />
    </div>
  ) : null;

  const resend = resendState(channel, {
    secondsLeft: cooldownSeconds,
    verifying: isVerifying,
    expired: isCodeExpired(verifyFailure),
    blocked: textBlocked,
  });
  const askedError = (asked: GrantMethod): string | null =>
    otherChannelError?.asked === asked ? otherChannelError.message : null;
  const title = titleFor(method);
  const proof = lookup.correctionProof;
  const fixChannel: DestinationChannel =
    method === "sms_otp" ? "phone" : "email";
  const subheader = (
    <DestinationSlot>
      <DestinationLine
        method={method}
        phase={destinationPhase(channel)}
        destination={destination}
      />
      {proof !== null && onCorrect !== undefined && (
        <FixRow
          key={fixChannel}
          surface={surface}
          channel={fixChannel}
          display={destination}
          proof={proof}
          onSave={(value, zip) => onCorrect(fixChannel, value, zip)}
        />
      )}
    </DestinationSlot>
  );

  if (method === "email_link") {
    return (
      <GrantFrame
        embedded={embedded}
        title={title}
        subheader={subheader}
        headerPhone={supportLine}
      >
        {recaptchaWidget}
        <EmailVerifyBody
          resend={resend}
          onResend={requestMagicLink}
          sendFailed={shownFailure !== null}
          notice={destinationNotice()}
          text={
            textOffered
              ? {
                  disabled: sending !== null || recaptchaKey === null,
                  error: askedError("sms_otp"),
                  onText: requestSmsCode,
                }
              : null
          }
          onChangeDestination={onChangeDestination}
          call={{
            kind: supportKind,
            surface,
            emphasis: callLineEmphasis({
              emailOffered: true,
              sendFailed: false,
            }),
          }}
        />
      </GrantFrame>
    );
  }

  return (
    <GrantFrame
      embedded={embedded}
      title={title}
      subheader={subheader}
      headerPhone={supportLine}
    >
      {recaptchaWidget}
      <TextVerifyBody
        code={codeInputState({
          sending: channel.sending,
          delivered,
          verifying: isVerifying || sendingMethod === "sms_otp",
        })}
        codeNotice={codeNotice}
        codeMessage={codeMessage(
          verifyFailure,
          failureFallbackFor(emailOffered),
        )}
        verifying={isVerifying}
        resend={resend}
        onSubmit={verifyCode}
        onResend={requestSmsCode}
        onCodeEdited={() => {
          setVerifyFailure(null);
          setCodeNotice("");
        }}
        notice={destinationNotice()}
        email={
          emailOffered
            ? {
                emphasis: emailEmphasis({
                  textFailed: shownFailure !== null,
                  textDelivered: delivered,
                  elapsedMs,
                }),
                sending,
                error: askedError("email_link"),
                onEmail: requestMagicLink,
              }
            : null
        }
        onChangeDestination={onChangeDestination}
        call={{
          kind: supportKind,
          surface,
          emphasis: callLineEmphasis({
            emailOffered,
            sendFailed: shownFailure !== null,
          }),
        }}
      />
    </GrantFrame>
  );
}
